/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */

import type { FC, ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Chip } from '@packages/shared/src/ui/Chip/Chip';
import { Radio } from '@packages/shared/src/ui/Radio/Radio';
import { Image } from '@packages/shared/src/components/Image/Image';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { BasicFilterValueItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasicFilterValueItemFragmentFragmentDoc';
import { RatingStars } from '@packages/shared/src/ui/Rating/RatingStars';
import { CategoryFiltersMessages } from '../CategoryFilterStandalone/CategoryFilterStandalone';

/* GraphQL */ `
  fragment BasicFilterValueItemFragment on SearchFilterValueBase {
    id
    name
    count
    ... on SearchColorFilterValue {
      color
    }
    ... on SearchImageFilterValue {
      image
    }
    ... on SearchRatingFilterValue {
      rating
    }
  }
`;

export type BasicFilterValueItemProps = {
  maskedData: FragmentType<typeof BasicFilterValueItemFragmentFragmentDoc>;
  /** If `true`, renders radio buttons instead of checkboxes */
  isSingleSelection?: boolean | null;
  /** If `true`, this item is displayed as selected */
  isSelected?: boolean;
  /** Callback fired when this filter value item is clicked */
  onClick?: (itemId: string) => void;
};

const filterSelectionMessages = defineMessages({
  selectedItem: {
    id: 'search.filters.category-selected',
    defaultMessage: 'Ausgewählt',
  },
  notSelectedItem: {
    id: 'search.filters.category-not-selected',
    defaultMessage: 'Nicht ausgewählt',
  },
});

const CircularAddon = ({ children }: { children: ReactNode }) => (
  <div className="border-color-grey mr-1 size-3.5 overflow-hidden rounded-full border-2">
    {children}
  </div>
);

const ColorAddon = ({ color }: { color: string }) => (
  <CircularAddon>
    <div style={{ backgroundColor: color }} className="size-full" />
  </CircularAddon>
);

const ImageAddon = ({ url }: { url: string }) => (
  <CircularAddon>
    <Image src={url} alt="" width="28" height="28" />
  </CircularAddon>
);

const RatingAddon = ({ defaultValue }: { defaultValue: number }) => (
  <div className="mr-1">
    <RatingStars ratingValue={defaultValue} />
  </div>
);

/**
 * BasicFilterItem component description displayed in storybook
 * */
export const BasicFilterValueItem: FC<BasicFilterValueItemProps> = ({
  maskedData,
  isSingleSelection,
  isSelected: isActive,
  onClick,
}) => {
  const { formatMessage } = useIntl();
  const value = unmask<typeof BasicFilterValueItemFragmentFragmentDoc>(maskedData);
  const { id, name, count } = value;

  const IconComponent = isSingleSelection ? Radio : Checkbox;

  const labelId = `basic-filter-item-${id}`;
  const iconAriaLabel = isActive
    ? formatMessage(filterSelectionMessages.selectedItem)
    : formatMessage(filterSelectionMessages.notSelectedItem);
  const role = isSingleSelection ? 'radio' : 'checkbox';

  // TODO: discuss if tooltip should be displayed to enable reading overflowing text, or how text overflow should be handled in general
  // options would be ellipsis (like currently), wrapping (default behaviour of MUI), ...?
  return (
    <li className="flex">
      <div
        role={role}
        aria-checked={isActive}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onClick?.(id);
          }
        }}
        onClick={onClick && (() => onClick(id))}
        className="hover:bg-color-grey-light flex flex-1 items-center justify-between px-2 py-1 -outline-offset-1"
      >
        <div className="flex flex-2 items-center">
          <IconComponent
            aria-hidden
            aria-live="polite"
            aria-label={iconAriaLabel}
            readOnly
            checked={isActive}
            tabIndex={-1}
            aria-labelledby={labelId}
          />
          {'rating' in value && <RatingAddon defaultValue={value.rating} />}
          {'color' in value && <ColorAddon color={value.color} />}
          {'image' in value && <ImageAddon url={value.image} />}
          <p className="text-left" id={labelId}>
            {name}
          </p>
        </div>
        <Chip
          variant="filled"
          // hide Chip just visually, to keep consistent height (Chip forces 32px height)
          className={`${count === 0 ? 'opacity-0' : 'opacity-100'} pointer-events-none ml-1 border-0`}
          aria-label={formatMessage(CategoryFiltersMessages.itemsQuantity, { count })}
          label={String(count)}
        />
      </div>
    </li>
  );
};
